import {
  DestroyRef,
  Directive,
  effect,
  inject,
  input,
  output,
  signal,
  untracked,
  WritableSignal,
} from '@angular/core';
import { FieldTree } from '@angular/forms/signals';
import { ZoudashUtils } from '@workspace/data-types';
import { ApplicationStore } from '@workspace/ng-data-access';
import { DtoChangedDetection } from './form.abstract.component';

@Directive()
export abstract class AbstractSignalFormComponent<TDto extends object, TModel extends object = TDto> {
  protected destroyRef = inject(DestroyRef);
  protected appStore = inject(ApplicationStore);

  // Inputs
  dto = input<TDto | null | undefined>(undefined);
  requiredFields = input<string[]>([]);
  showOptionalFields = input<string[]>([]);
  allDisable = input(false);
  allReadonly = input(false);
  clearFormSignal = input<boolean>(false);

  // Outputs
  validityChanged = output<boolean>();
  dtoChanged = output<DtoChangedDetection<TDto>>();

  // Form State
  abstract model: WritableSignal<TModel>;
  abstract formTree: FieldTree<TModel>;

  formReady = signal(false);
  ogValue: TDto | undefined | null;

  debugClickCount = 0;
  debugClickTimer: ReturnType<typeof setTimeout> | undefined;

  private hasInitializedDto = false;
  private lastEmittedDto: TDto | undefined;
  private lastEmittedValidity: boolean | undefined;

  constructor() {
    // Sync incoming DTO input to the writable model signal
    effect(() => {
      const dtoValue = this.dto();
      if (!this.hasInitializedDto) {
        if (dtoValue !== undefined && dtoValue !== null) {
          this.ogValue =
            typeof structuredClone !== 'undefined'
              ? structuredClone(dtoValue)
              : JSON.parse(JSON.stringify(dtoValue));
          this.model.set(this.buildModelFromDto(dtoValue));
        } else {
          this.ogValue = dtoValue ?? null;
        }
        this.hasInitializedDto = true;
        this.formReady.set(true);
      }
    });

    // Notify validity changes
    effect(() => {
      if (this.formReady() && this.formTree) {
        const isValid = this.isFormValid();
        if (isValid !== this.lastEmittedValidity) {
          this.lastEmittedValidity = isValid;
          untracked(() => this.validityChanged.emit(isValid));
        }
      }
    });

    // Notify DTO value changes reactively
    effect(() => {
      if (this.formReady() && this.formTree) {
        const isValid = this.isFormValid();
        if (isValid) {
          const currentDto = this.buildDtoFromModel(this.model());
          if (currentDto !== undefined && !ZoudashUtils.isEqual(currentDto, this.lastEmittedDto)) {
            this.lastEmittedDto =
              typeof structuredClone !== 'undefined'
                ? structuredClone(currentDto)
                : JSON.parse(JSON.stringify(currentDto));
            untracked(() => this.emitDto(currentDto));
          }
        }
      }
    });

    // Reset when clearFormSignal triggers
    effect(() => {
      if (this.clearFormSignal()) {
        this.resetForm();
      }
    });
  }

  protected isFormValid(): boolean {
    return this.formTree ? this.formTree().valid() : true;
  }

  protected resetForm(): void {
    if (this.ogValue) {
      this.model.set(this.buildModelFromDto(this.ogValue));
    }
  }

  protected buildModelFromDto(dto: TDto): TModel {
    const initial = this.model ? this.model() : ({} as TModel);
    const cloned =
      typeof structuredClone !== 'undefined' ? structuredClone(dto) : JSON.parse(JSON.stringify(dto));
    return {
      ...initial,
      ...(cloned || {}),
    } as TModel;
  }

  protected buildDtoFromModel(modelValue: TModel): TDto {
    return modelValue as unknown as TDto;
  }

  protected isRequired(field: string): boolean {
    return this.requiredFields().includes(field);
  }

  protected isVisible(field: string): boolean {
    return this.showOptionalFields().includes(field);
  }

  protected emitDto(newDto: TDto): void {
    this.dtoChanged.emit({
      dto: newDto,
      changed: !ZoudashUtils.isEqual(newDto, this.ogValue),
    });
  }

  protected debugForm(componentName: string): void {
    if (this.appStore.debugMode()) {
      this.debugClickCount++;

      if (this.debugClickCount === 1) {
        this.debugClickTimer = setTimeout(() => {
          this.debugClickCount = 0;
        }, 500);
      }

      if (this.debugClickCount === 3) {
        clearTimeout(this.debugClickTimer);
        console.log(`Debug SignalForm ${componentName}:`, {
          value: this.formTree().value(),
          valid: this.formTree().valid(),
          errors: this.formTree().errors(),
        });
        this.debugClickCount = 0;
      }
    }
  }
}
